/**
 * Persistent geocoding cache + coarse location fallbacks.
 *
 * Goals:
 *  - Reverse/forward geocode lookups are cached in localStorage so refreshing
 *    or returning to the site does not re-hit the geocoding APIs.
 *  - When geolocation permission is denied (or unavailable) the app still gets
 *    a usable origin: the last known position, else a city-based estimate
 *    derived from the browser timezone / locale.
 */
import { CITY_COORDS } from "@/lib/geoLookup";

const REVERSE_KEY = "gloogum:geo:revcache";
const FORWARD_KEY = "gloogum:geo:fwdcache";
const TTL = 7 * 24 * 60 * 60 * 1000; // 7 days — place names rarely change
const MAX_ENTRIES = 200;

export interface PlaceInfo {
  city?: string | null;
  state?: string | null;
  country?: string | null;
  label?: string | null;
}

type Entry<T> = { value: T; at: number };
type Store<T> = Record<string, Entry<T>>;

function read<T>(key: string): Store<T> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function write<T>(key: string, store: Store<T>) {
  try {
    const keys = Object.keys(store);
    if (keys.length > MAX_ENTRIES) {
      // drop the oldest entries
      keys
        .sort((a, b) => (store[a].at || 0) - (store[b].at || 0))
        .slice(0, keys.length - MAX_ENTRIES)
        .forEach((k) => delete store[k]);
    }
    localStorage.setItem(key, JSON.stringify(store));
  } catch { /* quota — ignore */ }
}

function get<T>(key: string, id: string): T | null {
  const entry = read<T>(key)[id];
  if (!entry) return null;
  if (Date.now() - entry.at > TTL) return null;
  return entry.value;
}

function set<T>(key: string, id: string, value: T) {
  const store = read<T>(key);
  store[id] = { value, at: Date.now() };
  write(key, store);
}

/**
 * Round coordinates so nearby lookups share one cache entry.
 * City-level lookups bucket to ~1km; precise (street address) lookups keep
 * full precision so two nearby points never share an address label.
 */
function coordKey(lat: number, lng: number, precise = false) {
  const digits = precise ? 6 : 2;
  return `${precise ? "p:" : ""}${lat.toFixed(digits)},${lng.toFixed(digits)}`;
}

/**
 * Cached reverse geocode (coords -> place). Never throws.
 * Pass `precise` for street-level detail (address forms); omit it for
 * city-level lookups (weather, coarse labels).
 */
export async function reverseGeocodeCached(lat: number, lng: number, precise = false): Promise<PlaceInfo | null> {
  const id = coordKey(lat, lng, precise);
  const hit = get<PlaceInfo>(REVERSE_KEY, id);
  if (hit) return hit;
  try {
    const res = await fetch(
      precise
        ? `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=18&addressdetails=1`
        : `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=10`,
    );
    const data = await res.json();
    const a = data?.address ?? {};
    const place: PlaceInfo = {
      city: a.city || a.town || a.village || a.county || null,
      state: a.state || null,
      country: a.country || null,
      label: data?.display_name || null,
    };
    set(REVERSE_KEY, id, place);
    return place;
  } catch {
    return null;
  }
}

/** Cached forward geocode (free-text address -> coords). Never throws. */
export async function geocodeCached(query: string): Promise<{ lat: number; lng: number } | null> {
  const id = query.trim().toLowerCase();
  if (!id) return null;
  const hit = get<{ lat: number; lng: number }>(FORWARD_KEY, id);
  if (hit) return hit;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(id)}`,
    );
    const data = await res.json();
    const first = Array.isArray(data) ? data[0] : null;
    if (!first) return null;
    const coords = { lat: Number(first.lat), lng: Number(first.lon) };
    if (!Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) return null;
    set(FORWARD_KEY, id, coords);
    return coords;
  } catch {
    return null;
  }
}

/**
 * Coarse, offline estimate of where the user is, used when geolocation is
 * denied/unavailable and no cached position exists. Derived from the browser
 * timezone (e.g. "Africa/Lagos") and, failing that, the locale region.
 */
export function estimatePositionFromEnvironment(): { lat: number; lng: number; city: string } | null {
  if (typeof window === "undefined") return null;
  const candidates: string[] = [];
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const cityPart = tz.split("/").pop() || "";
    if (cityPart) candidates.push(cityPart.replace(/_/g, " ").toLowerCase());
  } catch { /* ignore */ }

  for (const c of candidates) {
    for (const [city, coords] of Object.entries(CITY_COORDS)) {
      if (c.includes(city) || city.includes(c)) {
        return { lat: coords[0], lng: coords[1], city: city.replace(/\b\w/g, (m) => m.toUpperCase()) };
      }
    }
  }
  return null;
}
